import { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, ImageList, ImageListItem, Stack, Button, Fade, Modal, styled, Grid } from '@mui/material';

// Util
import { AppConfig } from '../../constants/Constants';

// Hooks and services
import { getImages } from '../../services/images.service';

// Types
import { Image, ImageData } from '../../types/Images';
import { ResourceType } from '../../types/ResourceType';
import { UploadImageRequest } from '../../requestModels/uploadImageRequest';

// Components
import uploadForm from './forms/Upload/uploadForm';
import CustomModal from './withModal';
import withForm from './withForm';
import { tablePrimaryButtonSx } from '../../styles/tableButtonStyles';

const UploadForm = withForm(uploadForm);

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundcolor: 'red',
    },
  },
  img: {
    maxHeight: '95%',
    maxWidth: '95%',
  },
}));

const CustomImageStyle = styled(ImageListItem)(() => ({
  '&:hover': {
    cursor: 'pointer',
    opacity: 0.8,
    transform: 'scale3d(1.05, 1.05, 1)',
    border: `solid 5px`,
  },
}));

export function CustomImageList(props: {
  subjectId: string;
  subjectIdentifier: string;
  resourceType: ResourceType;
  imageCols: number;
  imageHeight: number;
}) {
  const classes = useStyles();
  const [readerImages, setReaderImages] = useState<ImageData[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [image, setImage] = useState('');
  const [isPreviewImage, setIsPreviewImage] = useState(false);

  const fetchImages = async () => {
    if (props.subjectId) {
      await getImages({
        resourceType: props.resourceType,
        subjectId: props.subjectId,
      } as UploadImageRequest).then((res: any) => {
        if (res.status === 200) {
          const images = res.data.map((img: Image) => {
            return {
              originalFilename: undefined,
              image: img,
            } as ImageData;
          });

          setReaderImages(images);
        }
      });
    }
  };

  useEffect(() => {
    fetchImages();
  }, [props.subjectId]);

  const previewImage = (imageUrl: string) => {
    if (imageUrl) {
      setImage(imageUrl);
      setIsPreviewImage(true);
    }
  };

  return (
    <>
      <Stack direction='row' spacing={1} marginTop={2} justifyContent='flex-start'>
        <Button
          onClick={() => {
            setIsOpen(true);
          }}
          variant='contained'
          size='small'
          sx={{ ...tablePrimaryButtonSx, ml: '9px' }}
        >
          Manage Images
        </Button>
      </Stack>

      <Box sx={{ padding: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12}>
            {readerImages && readerImages.length > 0 ? (
              <ImageList cols={props.imageCols} gap={4} rowHeight={props.imageHeight}>
                {readerImages.map((data: ImageData, index) => (
                  <CustomImageStyle key={index}>
                    <img
                      srcSet={`${AppConfig.baseImageUrl}${data.image.path}`}
                      src={`${AppConfig.baseImageUrl}${data.image.path}`}
                      loading='lazy'
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onClick={() => previewImage(`${AppConfig.baseImageUrl}${data.image.path}`)}
                    />
                  </CustomImageStyle>
                ))}
              </ImageList>
            ) : (
              <span> No Images set</span>
            )}
          </Grid>
        </Grid>
      </Box>

      <CustomModal
        title='Upload'
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        maxWidth='md'
        customAction={() => {
          return (
            <Button
              variant='contained'
              onClick={() => {
                fetchImages();
                setIsOpen(false);
              }}
              color='secondary'
              size='small'
            >
              Close
            </Button>
          );
        }}
      >
        {props.subjectId && (
          <UploadForm
            fileType='image'
            files={readerImages}
            resourceType={props.resourceType}
            subjectId={props.subjectId}
            subjectIdentifier={props.subjectIdentifier}
          />
        )}
      </CustomModal>
      <Modal className={classes.modal} open={isPreviewImage} onClose={() => setIsPreviewImage(false)} closeAfterTransition>
        <Fade in={isPreviewImage} timeout={500}>
          <img src={image} loading='lazy' className={classes.img} />
        </Fade>
      </Modal>
    </>
  );
}
